// SCR-14 시스템 설정 탭 (ADMIN 전용) — tb_config 카드 목록
// sensitive_keywords 등 배열형: chips 편집 · 메일 템플릿: 텍스트 편집 · 그 외: JSON textarea
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  CaretDown,
  CaretUp,
  ClockCounterClockwise,
  Gear,
  Plus,
  X,
} from '@phosphor-icons/react'
import { EmptyState } from '../../components/EmptyState'
import { SkeletonCards } from '../../components/Skeleton'
import { useToast } from '../../components/Toast'
import { fmtServerDate, fmtServerDateTime, fmtServerTime } from '../../lib/format'
import {
  useConfigHistory,
  useConfigList,
  useSaveConfig,
  type ConfigItem,
} from './api'

// 알려진 키 한국어 제목 (미등록 키는 키 그대로 노출)
const CONFIG_TITLES: Record<string, string> = {
  sensitive_keywords: '민감 키워드 (카카오 상담)',
  account_check_agencies: '계정 점검 기관 (월초 배치)',
  report_mail_subject: '보고서 메일 제목 템플릿',
  report_mail_body: '보고서 메일 본문 템플릿',
}

const CONFIG_ORDER = [
  'sensitive_keywords',
  'account_check_agencies',
  'report_mail_subject',
  'report_mail_body',
]

// 문자열 배열형 키 — chips 추가/삭제 UI로 편집 (백엔드 검증: 비어 있지 않은 문자열 배열)
const ARRAY_CHIP_KEYS = ['sensitive_keywords', 'account_check_agencies']

// 키별 chips 편집기 하단 안내 문구
const CHIP_HINTS: Record<string, string> = {
  sensitive_keywords:
    '카카오 상담 발화에 포함되면 자동으로 담당자 연결(민감 정보 응대 차단)됩니다.',
  account_check_agencies:
    '월초 계정 점검 배치가 자산의 대상 기관명에 이 키워드가 포함되면 점검 이슈를 생성합니다.',
}

// 텍스트 템플릿형 키 — JSON 문자열로 저장하되 UI는 일반 텍스트로 편집 (보고서 배치 발송 메일)
const TEXT_TEMPLATE_KEYS: Record<string, { multiline: boolean }> = {
  report_mail_subject: { multiline: false },
  report_mail_body: { multiline: true },
}

const TEMPLATE_VARS_HINT =
  '치환 변수: {고객사명} {기간} {연도} {월} {보고서유형} {담당자명} — 고객사별 커스텀(구독 설정)이 있으면 그쪽이 우선합니다.'

export function SystemConfigTab() {
  const { data: items, isLoading, isError, refetch } = useConfigList()

  const sorted = useMemo(() => {
    if (!items) return []
    return [...items].sort((a, b) => {
      const ai = CONFIG_ORDER.indexOf(a.key)
      const bi = CONFIG_ORDER.indexOf(b.key)
      if (ai !== -1 || bi !== -1) {
        return (ai === -1 ? CONFIG_ORDER.length : ai) - (bi === -1 ? CONFIG_ORDER.length : bi)
      }
      return a.key.localeCompare(b.key)
    })
  }, [items])

  if (isLoading) return <SkeletonCards count={2} />

  if (isError) {
    return (
      <EmptyState
        icon={<Gear size={36} />}
        title="시스템 설정을 불러오지 못했습니다"
        description="시스템 설정을 불러오지 못했습니다. 잠시 후 다시 시도하거나 관리자에게 문의하세요."
        action={
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-full border border-hairline px-4 py-2 text-sm font-medium text-bone hover:bg-elevate"
          >
            다시 시도
          </button>
        }
      />
    )
  }

  if (sorted.length === 0) {
    return (
      <EmptyState
        icon={<Gear size={36} />}
        title="등록된 설정이 없습니다"
        description="tb_config에 설정 항목이 아직 없습니다."
      />
    )
  }

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      {sorted.map((item) => (
        <ConfigCard key={item.key} item={item} />
      ))}
    </div>
  )
}

// ── 설정 카드 ────────────────────────────────────────────────────────
function ConfigCard({ item }: { item: ConfigItem }) {
  const { showToast } = useToast()
  const save = useSaveConfig()
  const [draft, setDraft] = useState(item.value)
  const [draftValid, setDraftValid] = useState(true)
  const [historyOpen, setHistoryOpen] = useState(false)

  // 저장/재조회로 서버 값이 바뀌면 드래프트 리셋
  useEffect(() => {
    setDraft(item.value)
    setDraftValid(true)
  }, [item.value])

  const dirty = draft !== item.value

  const handleSave = async () => {
    if (!draftValid) {
      showToast('저장할 수 없습니다 — 값이 유효하지 않습니다 (JSON 형식·빈 목록 확인).', 'danger')
      return
    }
    try {
      await save.mutateAsync({ key: item.key, value: draft })
      showToast(`'${item.key}' 설정이 저장되었습니다.`, 'success')
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail
      showToast(detail ?? '설정 저장에 실패했습니다.', 'danger')
    }
  }

  let editor: ReactNode
  if (ARRAY_CHIP_KEYS.includes(item.key)) {
    editor = (
      <KeywordChipsEditor
        value={draft}
        hint={CHIP_HINTS[item.key]}
        onChange={(next, valid) => {
          setDraft(next)
          setDraftValid(valid)
        }}
      />
    )
  } else if (item.key in TEXT_TEMPLATE_KEYS) {
    editor = (
      <TextTemplateEditor
        value={draft}
        multiline={TEXT_TEMPLATE_KEYS[item.key].multiline}
        onChange={(next, valid) => {
          setDraft(next)
          setDraftValid(valid)
        }}
      />
    )
  } else {
    editor = (
      <JsonEditor
        value={draft}
        onChange={(next, valid) => {
          setDraft(next)
          setDraftValid(valid)
        }}
      />
    )
  }

  return (
    <div className="flex flex-col rounded-3xl border border-hairline bg-graphite p-5">
      {/* 헤더 */}
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold text-bone">
          {CONFIG_TITLES[item.key] ?? item.key}
        </h3>
        <code className="rounded bg-elevate-strong px-1.5 py-0.5 font-mono text-[11px] text-ash">
          {item.key}
        </code>
        {item.is_default && (
          <span className="inline-flex rounded-full border border-amber-400/25 bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
            기본값 (미저장)
          </span>
        )}
      </div>
      {item.description && (
        <p className="mt-1 text-xs text-slatey">{item.description}</p>
      )}
      {!item.is_default && item.updated_at && (
        <p className="mt-1 text-xs text-slatey">
          최종 수정 {fmtServerDateTime(item.updated_at)}
          {item.updated_by_name ? ` · ${item.updated_by_name}` : ''}
        </p>
      )}

      {/* 편집기 */}
      <div className="mt-3 flex-1">{editor}</div>

      {/* 푸터: 이력 토글 + 저장 */}
      <div className="mt-4 flex items-center justify-between border-t border-hairline pt-3">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-ash hover:text-bone"
        >
          <ClockCounterClockwise size={14} />
          변경 이력
          {historyOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </button>
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={handleSave}
          className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {save.isPending ? '저장 중…' : '저장'}
        </button>
      </div>

      {historyOpen && <ConfigHistoryList configKey={item.key} />}
    </div>
  )
}

// ── 변경 이력 (접힘 해제 시 조회) ────────────────────────────────────
function ConfigHistoryList({ configKey }: { configKey: string }) {
  const { data: history, isLoading, isError } = useConfigHistory(configKey, true)

  if (isLoading) {
    return <p className="mt-3 text-xs text-slatey">이력을 불러오는 중…</p>
  }
  if (isError) {
    return (
      <p className="mt-3 text-xs text-rose-500">변경 이력을 불러오지 못했습니다.</p>
    )
  }
  if (!history || history.length === 0) {
    return <p className="mt-3 text-xs text-slatey">변경 이력이 없습니다.</p>
  }

  return (
    <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto rounded-lg bg-graphite-2 p-3">
      {history.map((h, idx) => (
        <li
          key={h.history_id ?? idx}
          className="border-b border-hairline pb-2 text-xs last:border-b-0 last:pb-0"
        >
          <p className="text-ash">
            {h.created_at ? `${fmtServerDate(h.created_at)} ${fmtServerTime(h.created_at)}` : '—'}
            {h.updated_by_name ? ` · ${h.updated_by_name}` : ''}
          </p>
          <p className="mt-0.5 break-all font-mono text-[11px] text-slatey">
            <span className="line-through">{truncate(h.old_value)}</span>
            <span className="mx-1 text-smoke">→</span>
            <span className="text-ash">{truncate(h.new_value)}</span>
          </p>
        </li>
      ))}
    </ul>
  )
}

function truncate(value?: string | null, max = 120): string {
  if (!value) return '(없음)'
  return value.length > max ? `${value.slice(0, max)}…` : value
}

// ── sensitive_keywords: chips 편집 ──────────────────────────────────
function parseKeywords(value: string): string[] {
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string')
  } catch {
    /* 파싱 실패 → 빈 목록에서 시작 */
  }
  return []
}

function KeywordChipsEditor({
  value,
  hint,
  onChange,
}: {
  value: string
  /** 편집기 하단 안내 문구 (키별 상이) */
  hint?: string
  /** 서버 검증(비어 있지 않은 문자열 배열)에 맞춰 빈 목록은 invalid */
  onChange: (serialized: string, valid: boolean) => void
}) {
  const keywords = useMemo(() => parseKeywords(value), [value])
  const [input, setInput] = useState('')

  const commit = (next: string[]) => onChange(JSON.stringify(next), next.length > 0)

  const add = () => {
    const kw = input.trim()
    if (!kw) return
    if (keywords.includes(kw)) {
      setInput('')
      return
    }
    commit([...keywords, kw])
    setInput('')
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {keywords.length === 0 && (
          <p className="text-xs text-slatey">등록된 키워드가 없습니다.</p>
        )}
        {keywords.map((kw) => (
          <span
            key={kw}
            className="inline-flex items-center gap-1 rounded-full border border-hairline bg-elevate-strong px-2.5 py-1 text-xs font-medium text-bone"
          >
            {kw}
            <button
              type="button"
              onClick={() => commit(keywords.filter((k) => k !== kw))}
              className="text-slatey hover:text-rose-400"
              aria-label={`${kw} 삭제`}
            >
              <X size={12} weight="bold" />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2.5 flex gap-1.5">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          placeholder="키워드 입력 후 Enter"
          className="h-9 flex-1 rounded-lg border border-hairline bg-graphite px-3 text-sm text-bone placeholder:text-slatey focus:border-white/30 focus:outline-none"
        />
        <button
          type="button"
          onClick={add}
          disabled={!input.trim()}
          className="flex h-9 items-center gap-1 rounded-full border border-hairline px-3 text-sm font-medium text-bone hover:bg-elevate disabled:opacity-40"
        >
          <Plus size={14} weight="bold" />
          추가
        </button>
      </div>
      {hint && <p className="mt-1.5 text-[11px] text-slatey">{hint}</p>}
    </div>
  )
}

// ── report_mail_subject/body: 텍스트 템플릿 편집 ────────────────────
// 서버는 JSON 문자열("...")로 저장/검증 — UI는 일반 텍스트로 편집하고 저장 시 직렬화.
function parseTemplateText(value: string): string {
  try {
    const parsed = JSON.parse(value)
    if (typeof parsed === 'string') return parsed
  } catch {
    /* JSON 문자열이 아니면 원문 그대로 */
  }
  return value
}

function TextTemplateEditor({
  value,
  multiline,
  onChange,
}: {
  value: string
  /** 본문(여러 줄) 여부 — 제목은 한 줄 input */
  multiline: boolean
  /** 서버 검증(비어 있지 않은 문자열)에 맞춰 공백만이면 invalid */
  onChange: (serialized: string, valid: boolean) => void
}) {
  const text = useMemo(() => parseTemplateText(value), [value])
  const commit = (next: string) => onChange(JSON.stringify(next), next.trim().length > 0)
  const valid = text.trim().length > 0
  const cls = `w-full rounded-lg border bg-graphite px-3 text-sm text-bone placeholder:text-slatey focus:outline-none ${
    valid ? 'border-hairline focus:border-white/30' : 'border-rose-400/40 focus:border-rose-400/60'
  }`

  return (
    <div>
      {multiline ? (
        <textarea
          value={text}
          onChange={(e) => commit(e.target.value)}
          rows={8}
          spellCheck={false}
          className={`${cls} py-2 leading-relaxed`}
        />
      ) : (
        <input
          type="text"
          value={text}
          onChange={(e) => commit(e.target.value)}
          className={`${cls} h-9`}
        />
      )}
      {!valid && (
        <p className="mt-1 text-[11px] text-rose-500">템플릿은 비워 둘 수 없습니다.</p>
      )}
      <p className="mt-1.5 text-[11px] text-slatey">{TEMPLATE_VARS_HINT}</p>
    </div>
  )
}

// ── 기타 키: JSON textarea ───────────────────────────────────────────
function JsonEditor({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string, valid: boolean) => void
}) {
  const valid = useMemo(() => {
    if (!value.trim()) return false
    try {
      JSON.parse(value)
      return true
    } catch {
      return false
    }
  }, [value])

  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => {
          const next = e.target.value
          let ok = false
          try {
            JSON.parse(next)
            ok = next.trim().length > 0
          } catch {
            ok = false
          }
          onChange(next, ok)
        }}
        rows={5}
        spellCheck={false}
        className={`w-full rounded-lg border bg-graphite p-3 font-mono text-xs text-bone focus:outline-none ${
          valid ? 'border-hairline focus:border-white/30' : 'border-rose-400/40 focus:border-rose-400/60'
        }`}
      />
      {!valid && (
        <p className="mt-1 text-[11px] text-rose-500">유효한 JSON 형식이 아닙니다.</p>
      )}
    </div>
  )
}
